import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { metricsService } from '../services/endpoints/metricsService';
import { format } from 'date-fns';
import CoachCommentThread from '../components/coach/CoachCommentThread';
import CheckInPhotoSection from '../components/metrics/CheckInPhotoSection';

const SCORES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const BOWEL_CONSISTENCY = ['Normal', 'Loose', 'Liquid'] as const;
type BowelConsistency = typeof BOWEL_CONSISTENCY[number];

// ── Reusable 1-10 tap-button row ──────────────────────────────────────────────
const ScoreRow: React.FC<{
  value: number | null | undefined;
  onChange: (v: number | null) => void;
  lowLabel: string;
  highLabel: string;
}> = ({ value, onChange, lowLabel, highLabel }) => (
  <div className="flex items-center gap-1 flex-wrap">
    <span className="text-sm text-gray-500 mr-1 w-16 text-right leading-tight">{lowLabel}</span>
    {SCORES.map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(value === n ? null : n)}
        className={`w-8 h-8 rounded-md text-sm font-semibold transition-colors ${
          value === n
            ? 'bg-gold-500 text-black'
            : 'bg-dark-700 border border-dark-600 text-gray-400 hover:border-gold-500 hover:text-white'
        }`}
      >
        {n}
      </button>
    ))}
    <span className="text-sm text-gray-500 ml-1 w-16 leading-tight">{highLabel}</span>
  </div>
);

// ── Yes / No toggle ────────────────────────────────────────────────────────────
const YesNo: React.FC<{
  value: boolean | null | undefined;
  onChange: (v: boolean | null) => void;
}> = ({ value, onChange }) => (
  <div className="flex gap-2">
    {([true, false] as const).map((opt) => (
      <button
        key={String(opt)}
        type="button"
        onClick={() => onChange(value === opt ? null : opt)}
        className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
          value === opt
            ? opt
              ? 'bg-green-600 text-white'
              : 'bg-dark-600 text-white border border-dark-500'
            : 'bg-dark-700 border border-dark-600 text-gray-400 hover:border-gray-500 hover:text-white'
        }`}
      >
        {opt ? 'Yes' : 'No'}
      </button>
    ))}
  </div>
);

// ── Main component ─────────────────────────────────────────────────────────────
const MetricsEntry: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialDate = searchParams.get('date') ?? format(new Date(), 'yyyy-MM-dd');
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const queryClient = useQueryClient();

  // ── Body & weight
  const [weight, setWeight]           = useState<string>('');
  const [steps, setSteps]             = useState<string>('');

  // ── Sleep & recovery
  const [sleepHours, setSleepHours]   = useState<string>('');
  const [sleepQuality, setSleepQuality] = useState<number | null>(null);
  const [hrv, setHrv]                 = useState<string>('');
  const [rhr, setRhr]                 = useState<string>('');

  // ── Vitals
  const [bpSys, setBpSys]             = useState<string>('');
  const [bpDia, setBpDia]             = useState<string>('');
  const [glucose, setGlucose]         = useState<string>('');

  // ── Wellbeing
  const [wellbeing, setWellbeing]     = useState<number | null>(null);

  // ── Bowel movement
  const [hadBM, setHadBM]             = useState<boolean | null>(null);
  const [bmConsistency, setBmConsistency] = useState<BowelConsistency | null>(null);

  // ── PED side effects
  const [pedSE, setPedSE]             = useState<boolean | null>(null);
  const [pedSENotes, setPedSENotes]   = useState<string>('');

  // ── Notes
  const [notes, setNotes]             = useState<string>('');

  // ── Load existing entry for the selected date ─────────────────────────────
  const { data: existingMetrics } = useQuery({
    queryKey: ['metrics', selectedDate],
    queryFn: () => metricsService.getMetricsByDate(selectedDate),
    retry: false,
    enabled: !!selectedDate,
  });

  // When existing data loads (or date changes), populate fields
  React.useEffect(() => {
    if (existingMetrics) {
      setWeight(existingMetrics.weight             != null ? String(existingMetrics.weight)                     : '');
      setSteps(existingMetrics.stepsYesterday      != null ? String(existingMetrics.stepsYesterday)             : '');
      setSleepHours(existingMetrics.sleepHours     != null ? String(existingMetrics.sleepHours)                 : '');
      setSleepQuality(existingMetrics.sleepQuality ?? null);
      setHrv(existingMetrics.overnightHrv          != null ? String(existingMetrics.overnightHrv)               : '');
      setRhr(existingMetrics.overnightRestingHeartRate != null ? String(existingMetrics.overnightRestingHeartRate) : '');
      setBpSys(existingMetrics.bloodPressureSystolic  != null ? String(existingMetrics.bloodPressureSystolic)   : '');
      setBpDia(existingMetrics.bloodPressureDiastolic != null ? String(existingMetrics.bloodPressureDiastolic)  : '');
      setGlucose(existingMetrics.glucoseLevel      != null ? String(existingMetrics.glucoseLevel)               : '');
      setWellbeing(existingMetrics.wellbeingScore  ?? null);
      setHadBM(existingMetrics.hadBowelMovement    ?? null);
      setBmConsistency((existingMetrics.bowelMovementConsistency as BowelConsistency) ?? null);
      setPedSE(existingMetrics.pedSideEffects      ?? null);
      setPedSENotes(existingMetrics.pedSideEffectsNotes ?? '');
      setNotes(existingMetrics.notes               ?? '');
    } else {
      // No entry for this date — reset all fields
      setWeight(''); setSteps(''); setSleepHours(''); setSleepQuality(null);
      setHrv(''); setRhr(''); setBpSys(''); setBpDia(''); setGlucose('');
      setWellbeing(null); setHadBM(null); setBmConsistency(null);
      setPedSE(null); setPedSENotes(''); setNotes('');
    }
    setIsDirty(false);
  }, [existingMetrics, selectedDate]);

  // ── Save mutation ──────────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: metricsService.saveMetrics,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metrics'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['metrics-today-check'] }); // clears check-in reminder badge
      setIsDirty(false);
      setSuccessMessage('Check-in saved!');
      setTimeout(() => setSuccessMessage(null), 3000);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveMutation.mutate({
      // carry existing fields (e.g. BF%, Height, LeanMass from historical data)
      ...existingMetrics,
      metricId: existingMetrics?.metricId,
      date: selectedDate,
      weight:                   weight     ? parseFloat(weight)     : undefined,
      stepsYesterday:           steps      ? parseInt(steps, 10)    : undefined,
      sleepHours:               sleepHours ? parseFloat(sleepHours) : undefined,
      sleepQuality:             sleepQuality ?? undefined,
      overnightHrv:             hrv        ? parseInt(hrv, 10)      : undefined,
      overnightRestingHeartRate: rhr       ? parseInt(rhr, 10)      : undefined,
      bloodPressureSystolic:    bpSys      ? parseInt(bpSys, 10)    : undefined,
      bloodPressureDiastolic:   bpDia      ? parseInt(bpDia, 10)    : undefined,
      glucoseLevel:             glucose    ? parseFloat(glucose)    : undefined,
      wellbeingScore:           wellbeing  ?? undefined,
      hadBowelMovement:         hadBM      ?? undefined,
      bowelMovementConsistency: hadBM === true ? (bmConsistency ?? undefined) : undefined,
      pedSideEffects:           pedSE      ?? undefined,
      pedSideEffectsNotes:      pedSE === true ? pedSENotes : undefined,
      notes:                    notes      || undefined,
    });
  };

  // ── Section header helper ──────────────────────────────────────────────────
  const SectionHeader = ({ label }: { label: string }) => (
    <div className="flex items-center gap-3 mb-3">
      <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-500">{label}</h3>
      <div className="flex-1 border-t border-dark-700" />
    </div>
  );

  const inputClass = "bg-dark-700 border border-dark-600 text-white rounded-lg px-3 py-2 text-sm w-full focus:border-gold-500 focus:outline-none placeholder-gray-600";

  return (
    <div className="max-w-3xl mx-auto">
      {/* Page header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gold-500">Full Check-In</h1>
          <p className="text-sm text-gray-400 mt-0.5">Log your daily check-in data</p>
        </div>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="bg-dark-700 border border-dark-600 text-white rounded-lg px-3 py-2 text-sm focus:border-gold-500 focus:outline-none"
        />
      </div>

      <form onSubmit={handleSubmit} onChange={() => setIsDirty(true)}>
        <div className="bg-dark-800 rounded-xl p-6 space-y-6">

          {/* ── Body & Activity ── */}
          <div>
            <SectionHeader label="Body & Activity" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Morning Weight (lbs)</label>
                <input type="number" step="0.1" value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  className={inputClass} placeholder="182.0" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Steps (yesterday)</label>
                <input type="number" step="1" min="0" value={steps}
                  onChange={(e) => setSteps(e.target.value)}
                  className={inputClass} placeholder="8500" />
              </div>
            </div>
          </div>

          {/* ── Sleep & Recovery ── */}
          <div>
            <SectionHeader label="Sleep & Recovery" />
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Sleep (hrs)</label>
                <input type="number" step="0.5" min="0" max="24" value={sleepHours}
                  onChange={(e) => setSleepHours(e.target.value)}
                  className={inputClass} placeholder="7.5" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Overnight HRV (ms)</label>
                <input type="number" min="0" value={hrv}
                  onChange={(e) => setHrv(e.target.value)}
                  className={inputClass} placeholder="65" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Resting Heart Rate (bpm)</label>
                <input type="number" min="0" value={rhr}
                  onChange={(e) => setRhr(e.target.value)}
                  className={inputClass} placeholder="52" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-2">Sleep Quality</label>
              <ScoreRow value={sleepQuality} onChange={setSleepQuality}
                lowLabel="😴 poor" highLabel="🌟 great" />
            </div>
          </div>

          {/* ── Vitals ── */}
          <div>
            <SectionHeader label="Vitals" />
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">BP Systolic</label>
                <input type="number" value={bpSys}
                  onChange={(e) => setBpSys(e.target.value)}
                  className={inputClass} placeholder="120" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">BP Diastolic</label>
                <input type="number" value={bpDia}
                  onChange={(e) => setBpDia(e.target.value)}
                  className={inputClass} placeholder="80" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Glucose (mg/dL)</label>
                <input type="number" step="0.1" value={glucose}
                  onChange={(e) => setGlucose(e.target.value)}
                  className={inputClass} placeholder="90" />
              </div>
            </div>
          </div>

          {/* ── Wellbeing ── */}
          <div>
            <SectionHeader label="Wellbeing" />
            <ScoreRow value={wellbeing} onChange={setWellbeing}
              lowLabel="😫 awful" highLabel="🔥 perfect" />
          </div>

          {/* ── Bowel Movement ── */}
          <div>
            <SectionHeader label="Bowel Movement Yesterday" />
            <YesNo value={hadBM} onChange={(v) => { setHadBM(v); if (!v) setBmConsistency(null); }} />
            {hadBM === true && (
              <div className="flex gap-2 mt-3">
                {BOWEL_CONSISTENCY.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setBmConsistency(bmConsistency === c ? null : c)}
                    className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                      bmConsistency === c
                        ? 'bg-gold-500 text-black'
                        : 'bg-dark-700 border border-dark-600 text-gray-400 hover:border-gold-500 hover:text-white'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── PED Side Effects ── */}
          <div>
            <SectionHeader label="PED Side Effects?" />
            <YesNo value={pedSE} onChange={(v) => { setPedSE(v); if (!v) setPedSENotes(''); }} />
            {pedSE === true && (
              <textarea
                value={pedSENotes}
                onChange={(e) => setPedSENotes(e.target.value)}
                rows={3}
                className="mt-3 bg-dark-700 border border-dark-600 text-white rounded-lg px-3 py-2 text-sm w-full focus:border-gold-500 focus:outline-none placeholder-gray-600"
                placeholder="Describe any side effects you're experiencing…"
              />
            )}
          </div>

          {/* ── Notes ── */}
          <div>
            <SectionHeader label="Notes (optional)" />
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="bg-dark-700 border border-dark-600 text-white rounded-lg px-3 py-2 text-sm w-full focus:border-gold-500 focus:outline-none placeholder-gray-600"
              placeholder="Anything else worth noting today…"
            />
          </div>

        </div>

        {/* Submit — success/error banners below buttons so they're visible on mobile */}
        <div className="mt-5 space-y-3">
          <div className="flex items-center gap-4">
            <button
              type="submit"
              disabled={saveMutation.isPending}
              className="px-6 py-2.5 bg-gold-500 hover:bg-gold-400 disabled:opacity-50 text-black font-semibold text-sm rounded-lg transition-colors"
            >
              {saveMutation.isPending ? 'Saving…' : 'Save Check-In'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/metrics')}
              className="px-6 py-2.5 bg-dark-700 hover:bg-dark-600 border border-dark-600 hover:border-gray-500 text-gray-300 hover:text-white font-semibold text-sm rounded-lg transition-colors"
            >
              {isDirty ? 'Cancel' : 'Close'}
            </button>
            {/* Hint shown only before the first save */}
            {!existingMetrics?.metricId && !saveMutation.isPending && (
              <span className="text-xs text-gray-600 italic">Save this check-in to attach photos</span>
            )}
          </div>

          {/* Confirmation — sits directly below the buttons, visible without scrolling */}
          {successMessage && (
            <div className="px-4 py-3 bg-green-600/20 border border-green-600/30 rounded-lg text-green-400 text-sm font-medium flex items-center gap-2">
              <span>✅</span> {successMessage}
            </div>
          )}
          {saveMutation.isError && (
            <div className="px-4 py-3 bg-red-600/20 border border-red-600/30 rounded-lg text-red-400 text-sm font-medium flex items-center gap-2">
              <span>❌</span> Failed to save. Please try again.
            </div>
          )}
        </div>
      </form>

      {/* Photos + coach notes — only shown when the entry has been saved (metricId exists) */}
      {existingMetrics?.metricId && (
        <>
          {/* Check-in photos — athlete can upload/delete, no viewAs needed on this page */}
          <div className="mt-6 bg-dark-800 rounded-xl px-6 py-4">
            <CheckInPhotoSection metricId={existingMetrics.metricId} />
          </div>

          <div className="mt-4 bg-dark-800 rounded-xl px-6 py-4">
            <CoachCommentThread
              contextType="DailyCheckIn"
              contextId={existingMetrics.metricId}
              label="check-in"
            />
          </div>
        </>
      )}
    </div>
  );
};

export default MetricsEntry;
